import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Download } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { libraryReportsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import type { OverdueReportRow } from '@/types/library';
import { formatDate, orDash } from '@/utils/format';
import { Money, Stat, overdueText } from './shared';

/**
 * What is late, worst first.
 *
 * <p>The fine column says what each loan <em>would</em> be charged if the book
 * came back today, worked out by the same rule that charges on return — not a
 * second copy of that rule living here. Nothing on this page is owed yet: the
 * fine is raised when the book is handed in, and saying otherwise on a list a
 * librarian reads aloud to a parent would be a real problem.
 */
export function OverdueLoansPage() {
  const report = useQuery({
    queryKey: queryKeys.library.report('overdue', undefined),
    queryFn: () => libraryReportsApi.overdue(),
  });

  const columns: Column<OverdueReportRow>[] = [
    {
      key: 'member',
      header: 'Borrower',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{orDash(row.memberName)}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.membershipNumber)}
          </span>
        </span>
      ),
    },
    {
      key: 'book',
      header: 'Book',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.title}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.accessionNumber)}
          </span>
        </span>
      ),
    },
    { key: 'issueDate', header: 'Issued', render: (row) => formatDate(row.issueDate) },
    { key: 'dueDate', header: 'Due', render: (row) => formatDate(row.dueDate) },
    {
      key: 'daysOverdue',
      header: 'Late by',
      className: 'text-right',
      render: (row) => (
        <span className="font-semibold text-[color:var(--sms-danger)]">
          {overdueText(row.daysOverdue)}
        </span>
      ),
    },
    {
      key: 'fine',
      header: 'If returned today',
      className: 'text-right',
      render: (row) => <Money value={row.fineIfReturnedToday} />,
    },
  ];

  const data = report.data;

  return (
    <div>
      <PageHeader
        title="Overdue"
        description="Books that were due back and have not come."
        icon={<AlarmClock className="h-5 w-5" aria-hidden="true" />}
        action={
          <Button variant="secondary" onClick={() => void libraryReportsApi.overdueCsv()}>
            <Download className="h-4 w-4" aria-hidden="true" />
            Download the chase list
          </Button>
        }
      />

      {data !== undefined && (
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <Stat
            label="Overdue loans"
            value={data.count}
            tone={data.count > 0 ? 'danger' : 'success'}
            hint={`As at ${formatDate(data.asOf)}`}
          />
          <Stat
            label="If everything came back today"
            value={<Money value={data.fineIfReturnedToday} />}
            tone="warning"
            hint="Not owed yet — a fine is raised on return"
          />
          <Stat
            label="Worst"
            value={
              data.rows.length === 0 ? '—' : overdueText(data.rows[0]?.daysOverdue ?? 0)
            }
            hint={data.rows.length === 0 ? 'Nothing is late' : orDash(data.rows[0]?.memberName)}
          />
        </div>
      )}

      <Card>
        <DataTable
          columns={columns}
          rows={data?.rows}
          rowKey={(row) => row.transactionId}
          loading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          emptyTitle="Nothing is late"
          emptyDescription="Every book that has gone out is still within its loan period."
        />
      </Card>
    </div>
  );
}
